/**
 * ConsentCheckboxGroup — autenticazione Phase 5 (IMPL-P5-008).
 *
 * Three GDPR consent checkboxes: TOS (required), Privacy (required),
 * Marketing (optional). Required entries display a small "(obbligatorio)"
 * suffix; the marketing entry is "(facoltativo)".
 *
 * Links to TOS and Privacy open in a new tab via `rel="noopener"` and
 * include a Lucide `ExternalLink` indicator.
 *
 * The component is a controlled-by-parent helper used inside
 * `RegistrationForm` with react-hook-form's `Controller` so it can plug
 * directly into the `consents` slice of the form schema.
 */
import { forwardRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TOS_URL, PRIVACY_URL } from '../lib/legal-copy';

export interface ConsentValue {
  tos: boolean;
  privacy: boolean;
  marketing: boolean;
}

interface ConsentCheckboxGroupProps {
  value: ConsentValue;
  onChange: (next: ConsentValue) => void;
  errors?: Partial<Record<'tos' | 'privacy' | 'marketing', string>>;
}

interface RowProps {
  id: string;
  required?: boolean;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  errorId?: string;
  errorText?: string;
  children: React.ReactNode;
}

function ConsentRow({
  id,
  required,
  checked,
  onCheckedChange,
  errorId,
  errorText,
  children,
}: RowProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(v === true)}
          aria-invalid={errorText ? true : undefined}
          aria-describedby={errorId}
          className="mt-0.5"
        />
        <Label htmlFor={id} className="text-sm font-normal leading-relaxed">
          {children}
          {required ? (
            <span className="ml-1 text-xs text-muted-foreground">(obbligatorio)</span>
          ) : (
            <span className="ml-1 text-xs text-muted-foreground">(facoltativo)</span>
          )}
        </Label>
      </div>
      {errorText ? (
        <p
          id={errorId}
          role="alert"
          className="ml-7 text-xs font-medium text-destructive"
        >
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

function LegalLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline-offset-4 hover:underline"
    >
      {label}
      <ExternalLink className="ml-1 inline h-3 w-3" aria-hidden="true" />
    </a>
  );
}

export const ConsentCheckboxGroup = forwardRef<HTMLDivElement, ConsentCheckboxGroupProps>(
  ({ value, onChange, errors }, ref) => {
    return (
      <div ref={ref} className="space-y-3">
        <ConsentRow
          id="consent-tos"
          required
          checked={value.tos}
          onCheckedChange={(v) => onChange({ ...value, tos: v })}
          errorId={errors?.tos ? 'consent-tos-error' : undefined}
          errorText={errors?.tos}
        >
          Accetto i <LegalLink href={TOS_URL} label="Termini di Servizio" />
        </ConsentRow>

        <ConsentRow
          id="consent-privacy"
          required
          checked={value.privacy}
          onCheckedChange={(v) => onChange({ ...value, privacy: v })}
          errorId={errors?.privacy ? 'consent-privacy-error' : undefined}
          errorText={errors?.privacy}
        >
          Accetto la <LegalLink href={PRIVACY_URL} label="Privacy Policy" />
        </ConsentRow>

        <ConsentRow
          id="consent-marketing"
          checked={value.marketing}
          onCheckedChange={(v) => onChange({ ...value, marketing: v })}
        >
          Acconsento a ricevere comunicazioni di marketing via email
        </ConsentRow>
      </div>
    );
  },
);

ConsentCheckboxGroup.displayName = 'ConsentCheckboxGroup';
